import { memo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { WORLD_DOTS } from "../../data/worldDots";

const LIME = "#B8FF18";
const BLUE = "#147DFF";

const CITIES = [
    { x: 160, y: 146, c: BLUE },
    { x: 294, y: 137, c: LIME },
    { x: 370, y: 331, c: BLUE },
    { x: 500, y: 107, c: LIME },
    { x: 537, y: 104, c: BLUE },
    { x: 509, y: 262, c: LIME },
    { x: 604, y: 96, c: BLUE },
    { x: 654, y: 181, c: LIME },
    { x: 702, y: 210, c: BLUE },
    { x: 788, y: 272, c: LIME },
    { x: 888, y: 151, c: BLUE },
    { x: 920, y: 352, c: LIME },
];

const ARCS = [
    "M160 146 Q 222 88 294 137",
    "M294 137 Q 392 56 500 107",
    "M500 107 Q 556 66 604 96",
    "M604 96 Q 662 118 654 181",
    "M654 181 Q 682 198 702 210",
    "M702 210 Q 758 226 788 272",
    "M788 272 Q 858 216 888 151",
    "M500 107 Q 478 192 509 262",
    "M509 262 Q 442 312 370 331",
    "M294 137 Q 302 244 370 331",
    "M888 151 Q 942 244 920 352",
];

const Dots = memo(() => (
    <g>
        {WORLD_DOTS.map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r="1.7" fill="rgba(244,246,244,0.22)" />
        ))}
    </g>
));
Dots.displayName = "Dots";

const Pulse = ({ path, dur, delay, color }) => (
    <circle r="2.6" fill={color}>
        <animateMotion dur={`${dur}s`} begin={`${delay}s`} repeatCount="indefinite" path={path} />
        <animate attributeName="opacity" values="0;0.85;0.85;0" keyTimes="0;0.08;0.88;1" dur={`${dur}s`} begin={`${delay}s`} repeatCount="indefinite" />
    </circle>
);

const CityNode = ({ x, y, color, delay }) => {
    const reduce = useReducedMotion();
    return (
        <g>
            <motion.circle
                cx={x}
                cy={y}
                r={8}
                fill={color}
                opacity={0.1}
                animate={reduce ? undefined : { opacity: [0.04, 0.18, 0.04], scale: [1, 1.7, 1] }}
                transition={{ duration: 3.4, repeat: Infinity, delay, ease: "easeInOut" }}
                style={{ transformOrigin: `${x}px ${y}px` }}
            />
            <motion.circle
                cx={x}
                cy={y}
                r={3}
                fill={color}
                animate={reduce ? undefined : { opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2.8, repeat: Infinity, delay, ease: "easeInOut" }}
            />
        </g>
    );
};

export const WorldNetwork = ({ className = "" }) => {
    const reduce = useReducedMotion();
    return (
        <svg viewBox="0 0 1000 556" className={className} role="img" aria-label="Global Nexora network map with connected nodes">
            <defs>
                <radialGradient id="wn-haze" cx="50%" cy="46%" r="60%">
                    <stop offset="0%" stopColor={BLUE} stopOpacity="0.07" />
                    <stop offset="60%" stopColor={LIME} stopOpacity="0.03" />
                    <stop offset="100%" stopColor="#060809" stopOpacity="0" />
                </radialGradient>
            </defs>

            <rect x="0" y="0" width="1000" height="556" fill="url(#wn-haze)" />
            <Dots />

            <g fill="none" strokeWidth="1">
                {ARCS.map((d, i) => (
                    <path key={i} d={d} stroke={i % 2 === 0 ? LIME : BLUE} strokeOpacity="0.38" />
                ))}
            </g>

            {!reduce &&
                ARCS.slice(0, 8).map((d, i) => (
                    <Pulse key={i} path={d} dur={6.5 + i * 1.1} delay={i * 1.4} color={i % 2 === 0 ? LIME : BLUE} />
                ))}

            {CITIES.map((c, i) => (
                <CityNode key={i} x={c.x} y={c.y} color={c.c} delay={i * 0.45} />
            ))}
        </svg>
    );
};
